import type { Npc } from '../../types/progression'

/**
 * NPC de las guías de progresión, identificados por su ID en 06_monsters.
 * Identidad ≠ ubicación: la ubicación se resuelve en utils/npcLocation.ts con este orden:
 * administración → apariciones permanentes configuradas → referencia (con fuente) → no documentada.
 * No confundir con monstruos de nombre parecido (Goblin, Werewolf).
 *
 * Las referencias son de MU Online Season 6 clásico y quedan "pendientes de validar en Vanguard".
 */
export const npcs: Npc[] = [
  {
    id: 'sebina',
    name: 'Sebina the Priest',
    monsterId: 235,
    role: 'Segunda clase (misión de cambio de clase)',
    aliases: ['sebina', 'sebina the priest', 'sacerdotisa', 'sacerdotisa sebina', 'priestess sebina'],
    classicMaps: ['Devias'],
    reference: {
      points: [{ mapName: 'Devias', x: 183, y: 32 }],
      source: 'Mu Online Guide — Quest 2nd Class: Sebina The Priest',
      url: 'https://guidemuonline.com/guides/class-change/quest-2nd-class-sebina-the-priest',
    },
    sectionPath: '/guia/evoluciones#sebina',
  },
  {
    id: 'marlon',
    name: 'Marlon',
    monsterId: 229,
    role: 'Misión de Marlon y desbloqueo del combo',
    aliases: ['marlon', 'quest marlon', 'mision marlon'],
    classicMaps: ['Lorencia', 'Devias', 'Noria', 'Atlans'],
    reference: {
      points: [
        { mapName: 'Lorencia', x: 137, y: 87 },
        { mapName: 'Devias', x: 198, y: 47 },
        { mapName: 'Noria', x: 169, y: 89 },
        { mapName: 'Atlans', x: 17, y: 25 },
      ],
      condition: 'Se desplaza entre estos cuatro mapas: está en uno de ellos a la vez, no en todos.',
      source: 'Mu Online Guide — Quest 2nd Class: Marlon',
      url: 'https://guidemuonline.com/guides/class-change/quest-marlon',
    },
    sectionPath: '/guia/evoluciones#marlon',
  },
  {
    id: 'devin',
    name: 'Apostle Devin',
    monsterId: 406,
    role: 'Tercera evolución',
    aliases: ['devin', 'apostle devin', 'apostol devin'],
    classicMaps: ['Crywolf'],
    reference: {
      points: [{ mapName: 'Crywolf', x: 228, y: 48 }],
      source: 'MU Online News — [Quest 3rd Class] Apostle Devin',
      url: 'https://muonline.news/game-guides/class-change/quest-3rd-class-apostle-devin',
    },
    sectionPath: '/guia/evoluciones#tercera',
  },
  {
    id: 'quarel',
    name: 'Werewolf Quarel',
    monsterId: 407,
    role: 'Tercera evolución · paso hacia Barracks',
    aliases: ['werewolf quarel', 'werewolf quarrel', 'quarel', 'quarrel', 'npc barracks', 'acceso barracks'],
    classicMaps: ['Crywolf'],
    reference: {
      points: [{ mapName: 'Crywolf', x: 63, y: 239 }],
      source: 'Mu Online Guides — 3rd Class Quest',
      url: 'https://www.guidesmu.com/3rd-class-quest/',
    },
    sectionPath: '/guia/evoluciones#tercera-etapa-2',
  },
  {
    id: 'gatekeeper',
    name: 'Gatekeeper',
    monsterId: 408,
    role: 'Tercera evolución · paso hacia Refuge',
    aliases: ['gatekeeper', 'gate keeper', 'guardian de la puerta', 'npc refuge', 'acceso refuge'],
    classicMaps: ['Barracks'],
    reference: {
      points: [{ mapName: 'Barracks', x: 120, y: 168 }],
      source: 'Mu Online Guides — 3rd Class Quest',
      url: 'https://www.guidesmu.com/3rd-class-quest/',
    },
    sectionPath: '/guia/evoluciones#tercera-etapa-3',
  },
  {
    id: 'chaos-goblin',
    name: 'Chaos Goblin',
    monsterId: 238,
    role: 'Combinaciones de la Chaos Machine (armas Chaos, alas, capas)',
    aliases: ['chaos goblin', 'chaos machine', 'maquina chaos', 'maquina del chaos', 'goblin del chaos'],
    classicMaps: ['Noria'],
    // Solo el mapa: no se encontró una coordenada Season 6 documentada compatible.
    reference: {
      points: [{ mapName: 'Noria' }],
      source: 'Mu Online Guide — Noria',
      url: 'https://guidemuonline.com/maps/noria',
    },
    sectionPath: '/guia/alas',
  },
  {
    id: 'pet-trainer',
    name: 'Trainer',
    // Confirmado por el staff: "Pet Trainer" y "Trainer" son el mismo NPC; en la guía se llama Trainer (226).
    monsterId: 226,
    role: 'Resurrección de espíritus: Dark Horse y Dark Raven',
    aliases: ['pet trainer', 'trainer', 'entrenador', 'entrenador de mascotas', 'resurrect spirit', 'resucitar espiritu'],
    classicMaps: ['Lorencia'],
    reference: { points: [{ mapName: 'Lorencia', x: 122, y: 110 }], source: 'NPC de MU Online — Webzen' },
    sectionPath: '/guia/combinaciones#mascotas',
    services: [{ kind: 'combinaciones', status: 'clasica', note: 'Dark Horse y Dark Raven' }],
  },

  // ── NPC y tiendas · Lorencia y Noria. Función y mapa de MU Online Season 6, pendientes de validar en Vanguard ──
  {
    id: 'hanzo',
    name: 'Hanzo the Blacksmith',
    monsterId: 251,
    role: 'Herrero: venta de armas y armaduras, reparación',
    aliases: ['hanzo', 'hanzo the blacksmith', 'herrero', 'herrero de lorencia', 'blacksmith', 'tienda de armas', 'tienda de armaduras', 'armero', 'reparar', 'reparacion', 'reparar equipo'],
    classicMaps: ['Lorencia'],
    reference: { points: [{ mapName: 'Lorencia' }], source: 'Mu Online Guide — Lorencia', url: 'https://guidemuonline.com/maps/lorencia' },
    sectionPath: '/guia/npc#hanzo',
    services: [
      { kind: 'venta', status: 'clasica', note: 'Armas y armaduras' },
      { kind: 'reparacion', status: 'clasica' },
    ],
  },
  {
    id: 'pasi',
    name: 'Pasi the Mage',
    monsterId: 254,
    role: 'Mago vendedor: pergaminos y bastones',
    aliases: ['pasi', 'pasi the mage', 'mago vendedor', 'mago de lorencia', 'tienda de magia', 'pergaminos', 'scrolls', 'bastones'],
    classicMaps: ['Lorencia'],
    reference: { points: [{ mapName: 'Lorencia' }], source: 'Mu Online Guide — Lorencia', url: 'https://guidemuonline.com/maps/lorencia' },
    sectionPath: '/guia/npc#pasi',
    services: [{ kind: 'venta', status: 'clasica', note: 'Pergaminos de hechizo y bastones' }],
  },
  {
    id: 'lala',
    name: 'Elf Lala',
    monsterId: 242,
    role: 'Vendedora de Noria: pociones y objetos para elfas',
    aliases: ['lala', 'elf lala', 'elfa lala', 'vendedora de noria', 'pociones', 'tienda de pociones', 'flechas', 'arrows'],
    classicMaps: ['Noria'],
    reference: { points: [{ mapName: 'Noria' }], source: 'Mu Online Guide — Noria', url: 'https://guidemuonline.com/maps/noria' },
    sectionPath: '/guia/npc#lala',
    services: [{ kind: 'venta', status: 'clasica', note: 'Pociones, flechas y objetos de elfa' }],
  },
  {
    id: 'eo',
    name: 'Eo the Craftsman',
    monsterId: 243,
    role: 'Artesano de Noria: venta de equipo, reparación',
    aliases: ['eo', 'eo the craftsman', 'artesano', 'artesano de noria', 'craftsman', 'tienda de noria', 'arcos', 'reparar'],
    classicMaps: ['Noria'],
    reference: { points: [{ mapName: 'Noria' }], source: 'Mu Online Guide — Noria', url: 'https://guidemuonline.com/maps/noria' },
    sectionPath: '/guia/npc#eo',
    services: [
      { kind: 'venta', status: 'clasica', note: 'Arcos, ballestas y armaduras' },
      { kind: 'reparacion', status: 'clasica' },
    ],
  },
  // Robot (805): cuatro NPC con el mismo ID, uno por ciudad. Ubicación y función confirmadas por el staff (11/10/2026).
  ...robots(),
]

function robots(): Npc[] {
  const ROBOT = ['robot', 'npc robot', 'el robot', 'donde esta el robot', 'robot vendedor', 'robots']
  const SETS = ['vendedor de sets', 'tienda de sets', 'set dk', 'sets dk', 'set dark knight', 'set dw', 'sets dw', 'set dark wizard', 'set mg', 'sets mg', 'set magic gladiator', 'set rf', 'sets rf', 'set rage fighter', 'comprar set', 'donde compro set']
  const setsNote = 'Sets de Dark Knight, Dark Wizard, Magic Gladiator y Rage Fighter'
  const make = (city: string, role: string, note: string, extra: string[]): Npc => ({
    id: `robot-${city.toLowerCase()}`,
    name: `Robot (${city})`,
    monsterId: 805,
    adminKey: `npc-location:805:${city.toLowerCase()}`,
    role,
    aliases: [...ROBOT, `robot de ${city.toLowerCase()}`, `robot ${city.toLowerCase()}`, ...extra],
    classicMaps: [city],
    sectionPath: `/guia/npc#robot-${city.toLowerCase()}`,
    services: [{ kind: 'venta', status: 'admin', note }],
  })
  return [
    make('Lorencia', 'Vende sets de DK, DW, MG y RF', setsNote, SETS),
    make('Devias', 'Vende sets de DK, DW, MG y RF (igual que el de Lorencia)', setsNote, SETS),
    make('Noria', 'Vende objetos de elfa', 'Objetos de Fairy Elf', ['cosas de elfa', 'set elfa', 'sets elfa', 'set elf', 'tienda de elfa', 'items de elfa']),
    make('Elbeland', 'Vende objetos de Summoner', 'Objetos de Summoner', ['cosas de summoner', 'set summoner', 'sets summoner', 'set sum', 'tienda de summoner', 'items de summoner']),
  ]
}

const SERVICES: Record<string, Npc['services']> = {
  sebina: [{ kind: 'misiones', status: 'clasica', note: 'Segunda clase' }],
  marlon: [{ kind: 'misiones', status: 'clasica', note: 'Misión de Marlon y combo' }],
  devin: [{ kind: 'misiones', status: 'clasica', note: 'Tercera evolución' }],
  quarel: [{ kind: 'acceso', status: 'clasica', note: 'Paso hacia Barracks' }],
  gatekeeper: [{ kind: 'acceso', status: 'clasica', note: 'Paso hacia Refuge' }],
  'chaos-goblin': [{ kind: 'combinaciones', status: 'clasica', note: 'Chaos Machine' }],
}
npcs.forEach((n) => {
  if (!n.services && SERVICES[n.id]) n.services = SERVICES[n.id]
})

export const getNpc = (id: string) => npcs.find((n) => n.id === id)
